/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import {BaseFields, BaseModel, BaseProps, createFieldDef, FieldDefinition, ModelFieldType} from './baseModel';
import {IndexedObject} from '@dvrd/dvr-controls';
import Account from "./accountModel";
import {FilenameBase, InvoiceUploadRule, UnlayerDesign} from "../util/interfaces";
import {deepClone} from "../util/utils";

interface Props extends BaseProps {
    reply_email?: string;
    sender_name?: string;
    invoice_prefix?: string;
    invoice_start?: string;
    use_bol_text?: boolean;
    use_platform_text?: boolean;
    email_template?: string;
    email_subject?: string;
    email_bcc?: string;
    history_sent?: boolean;
    send_canceled_invoices?: boolean;
    enable_mail_tracking?: boolean;
    email_max_product_name_length?: number | null;
    include_ubl_attachment?: boolean | null;
    auto_create_credit_invoices?: boolean;
    auto_send_credit_invoices?: boolean;
    credit_invoice_num_prefix?: string;
    credit_invoice_num_start?: string;
    reverse_vat_countries?: string[];
    manual_email_template?: string;
    manual_mail_json?: IndexedObject<any>;
    filename_base?: FilenameBase;
    proactively_upload_invoices?: boolean;
    auto_respond_invoice_requests?: boolean;
    invoice_upload_rules?: Array<InvoiceUploadRule>;
    notify_missing_invoice?: boolean;
    notify_missing_invoice_mail?: string;
    auto_respond_old_orders?: boolean;
    old_order_invoice_prefix?: string;
    old_order_invoice_start?: string;
    participate_kor?: boolean;
    account_id?: string;
    account?: Account | any;
}

export interface SettingsFields extends BaseFields {
    reply_email: string;
    sender_name: string;
    invoice_prefix: string;
    invoice_start: string;
    use_bol_text: boolean;
    use_platform_text: boolean;
    email_template: string;
    email_subject: string;
    email_bcc: string;
    history_sent: boolean;
    send_canceled_invoices: boolean;
    enable_mail_tracking: boolean;
    email_max_product_name_length: number | null;
    include_ubl_attachment: boolean;
    auto_create_credit_invoices: boolean;
    auto_send_credit_invoices: boolean;
    credit_invoice_num_prefix: string;
    credit_invoice_num_start: string;
    reverse_vat_countries: string[];
    manual_email_template: string;
    manual_mail_json: UnlayerDesign;
    filename_base: FilenameBase;
    proactively_upload_invoices: boolean;
    auto_respond_invoice_requests: boolean;
    invoice_upload_rules: Array<InvoiceUploadRule>;
    notify_missing_invoice: boolean;
    notify_missing_invoice_mail: string;
    auto_respond_old_orders: boolean;
    old_order_invoice_prefix: string;
    old_order_invoice_start: string;
    participate_kor: boolean;
    account_id: string;
    account: Account | null;
}

const fields: SettingsFields = {
    id: '',
    created_at: '',
    updated_at: '',
    reply_email: '',
    sender_name: '',
    invoice_prefix: '{jaar}-',
    invoice_start: '0001',
    use_bol_text: true,
    use_platform_text: true,
    email_template: '',
    email_subject: 'Factuur {order_id} van {bedrijf_naam}',
    email_bcc: '',
    history_sent: false,
    send_canceled_invoices: false,
    enable_mail_tracking: false,
    email_max_product_name_length: null,
    include_ubl_attachment: false,
    auto_create_credit_invoices: false,
    auto_send_credit_invoices: false,
    credit_invoice_num_prefix: '{jaar}-',
    credit_invoice_num_start: '0001',
    reverse_vat_countries: [],
    manual_email_template: '',
    manual_mail_json: {} as UnlayerDesign,
    filename_base: FilenameBase.INVOICE_NUMBER,
    proactively_upload_invoices: false,
    auto_respond_invoice_requests: false,
    invoice_upload_rules: [],
    notify_missing_invoice: false,
    notify_missing_invoice_mail: '',
    auto_respond_old_orders: false,
    old_order_invoice_prefix: '{jaar}pre-',
    old_order_invoice_start: '0001',
    participate_kor: false,
    account_id: '',
    account: null,
};

const fieldsDefinition: { [key in keyof Omit<SettingsFields, 'account'>]: FieldDefinition } = {
    id: createFieldDef(ModelFieldType.STRING),
    created_at: createFieldDef(ModelFieldType.STRING),
    updated_at: createFieldDef(ModelFieldType.STRING, undefined, true),
    reply_email: createFieldDef(ModelFieldType.STRING),
    sender_name: createFieldDef(ModelFieldType.STRING),
    invoice_prefix: createFieldDef(ModelFieldType.STRING, '{jaar}-'),
    invoice_start: createFieldDef(ModelFieldType.STRING, '0001'),
    use_bol_text: createFieldDef(ModelFieldType.BOOLEAN, true),
    use_platform_text: createFieldDef(ModelFieldType.BOOLEAN, true),
    email_template: createFieldDef(ModelFieldType.STRING),
    email_subject: createFieldDef(ModelFieldType.STRING, 'Factuur {order_id} van {bedrijf_naam}'),
    account_id: createFieldDef(ModelFieldType.STRING),
    email_bcc: createFieldDef(ModelFieldType.STRING),
    history_sent: createFieldDef(ModelFieldType.BOOLEAN),
    send_canceled_invoices: createFieldDef(ModelFieldType.BOOLEAN),
    enable_mail_tracking: createFieldDef(ModelFieldType.BOOLEAN),
    email_max_product_name_length: createFieldDef(ModelFieldType.NUMBER, null, true),
    include_ubl_attachment: createFieldDef(ModelFieldType.BOOLEAN),
    credit_invoice_num_prefix: createFieldDef(ModelFieldType.STRING, '{jaar}-'),
    credit_invoice_num_start: createFieldDef(ModelFieldType.STRING, '0001'),
    auto_create_credit_invoices: createFieldDef(ModelFieldType.BOOLEAN, true),
    auto_send_credit_invoices: createFieldDef(ModelFieldType.BOOLEAN, true),
    reverse_vat_countries: createFieldDef(ModelFieldType.ARRAY),
    manual_mail_json: createFieldDef(ModelFieldType.OBJECT),
    manual_email_template: createFieldDef(ModelFieldType.STRING),
    filename_base: createFieldDef(ModelFieldType.STRING, FilenameBase.INVOICE_NUMBER),
    proactively_upload_invoices: createFieldDef(ModelFieldType.BOOLEAN),
    auto_respond_invoice_requests: createFieldDef(ModelFieldType.BOOLEAN),
    invoice_upload_rules: createFieldDef(ModelFieldType.ARRAY),
    notify_missing_invoice_mail: createFieldDef(ModelFieldType.STRING),
    notify_missing_invoice: createFieldDef(ModelFieldType.BOOLEAN),
    auto_respond_old_orders: createFieldDef(ModelFieldType.BOOLEAN),
    old_order_invoice_prefix: createFieldDef(ModelFieldType.STRING, '{jaar}pre-'),
    old_order_invoice_start: createFieldDef(ModelFieldType.STRING, '0001'),
    participate_kor: createFieldDef(ModelFieldType.BOOLEAN),
};

const ignoreProps: Array<keyof Props> = ['account'];

export default class Settings extends BaseModel<Props, SettingsFields> {
    constructor(props?: Props) {
        super(ignoreProps, fieldsDefinition, fields, props);
        this.build(props);
    }

    get id(): string {
        return this.fields.id as string;
    }

    get replyEmail(): string {
        return this.fields.reply_email;
    }

    set replyEmail(reply_email: string) {
        this.fields.reply_email = reply_email;
    }

    get senderName(): string {
        return this.fields.sender_name;
    }

    set senderName(sender_name: string) {
        this.fields.sender_name = sender_name;
    }

    get invoicePrefix(): string {
        return this.fields.invoice_prefix;
    }

    set invoicePrefix(invoice_prefix: string) {
        this.fields.invoice_prefix = invoice_prefix;
    }

    get invoiceStart(): string {
        return this.fields.invoice_start;
    }

    set invoiceStart(invoice_start: string) {
        this.fields.invoice_start = invoice_start;
    }

    get useBolText(): boolean {
        return this.fields.use_bol_text;
    }

    set useBolText(use_bol_text: boolean) {
        this.fields.use_bol_text = use_bol_text;
    }

    get usePlatformText(): boolean {
        return this.fields.use_platform_text;
    }

    set usePlatformText(use_platform_text: boolean) {
        this.fields.use_platform_text = use_platform_text;
    }

    get emailTemplate(): string {
        return this.fields.email_template;
    }

    set emailTemplate(emailTemplate: string) {
        this.fields.email_template = emailTemplate;
    }

    get emailSubject(): string {
        return this.fields.email_subject;
    }

    set emailSubject(subject: string) {
        this.fields.email_subject = subject;
    }

    get accountId(): string {
        return this.fields.account_id;
    }

    set accountId(account_id: string) {
        this.fields.account_id = account_id;
    }

    get account(): Account {
        return this.fields.account!;
    }

    set account(account: Account) {
        this.fields.account = account;
        this.accountId = account.id;
    }

    get emailBcc(): string {
        return this.fields.email_bcc;
    };

    set emailBcc(bcc: string) {
        this.fields.email_bcc = bcc;
    }

    get historySent(): boolean {
        return this.fields.history_sent;
    }

    set historySent(sent: boolean) {
        this.fields.history_sent = sent;
    }

    get sendCanceledInvoices(): boolean {
        return this.fields.send_canceled_invoices;
    }

    set sendCanceledInvoices(send_canceled_invoices: boolean) {
        this.fields.send_canceled_invoices = send_canceled_invoices;
    }

    get enableMailTracking(): boolean {
        return this.fields.enable_mail_tracking;
    }

    set enableMailTracking(enable_mail_tracking: boolean) {
        this.fields.enable_mail_tracking = enable_mail_tracking;
    }

    get autoCreateCreditInvoices(): boolean {
        return this.fields.auto_create_credit_invoices;
    }

    set autoCreateCreditInvoices(create: boolean) {
        this.fields.auto_create_credit_invoices = create;
    }

    get autoSendCreditInvoices(): boolean {
        return this.fields.auto_send_credit_invoices;
    }

    set autoSendCreditInvoices(send: boolean) {
        this.fields.auto_send_credit_invoices = send;
    }

    get creditInvoiceNumPrefix(): string {
        return this.fields.credit_invoice_num_prefix;
    }

    set creditInvoiceNumPrefix(prefix: string) {
        this.fields.credit_invoice_num_prefix = prefix;
    }

    get creditInvoiceNumStart(): string {
        return this.fields.credit_invoice_num_start;
    }

    set creditInvoiceNumStart(start: string) {
        this.fields.credit_invoice_num_start = start;
    }

    get emailMaxProductNameLength(): number | null {
        return this.fields.email_max_product_name_length;
    }

    set emailMaxProductNameLength(max_length: number | null) {
        this.fields.email_max_product_name_length = max_length;
    }

    get reverseVatCountries(): string[] {
        return this.fields.reverse_vat_countries;
    }

    set reverseVatCountries(reverseCountries: string[]) {
        this.fields.reverse_vat_countries = reverseCountries;
    }

    get manualMailTemplate(): string {
        return this.fields.manual_email_template;
    }

    set manualMailTemplate(template: string) {
        this.fields.manual_email_template = template;
    }

    get manualMailJson(): UnlayerDesign {
        return this.fields.manual_mail_json;
    }

    set manualMailJson(json: UnlayerDesign) {
        this.fields.manual_mail_json = json;
    }

    get filenameBase(): FilenameBase {
        return this.fields.filename_base;
    }

    set filenameBase(value: FilenameBase) {
        this.fields.filename_base = value;
    }

    get proactivelyUploadInvoices(): boolean {
        return this.fields.proactively_upload_invoices;
    }

    set proactivelyUploadInvoices(value: boolean) {
        this.fields.proactively_upload_invoices = value;
    }

    get autoRespondInvoiceRequests(): boolean {
        return this.fields.auto_respond_invoice_requests;
    }

    set autoRespondInvoiceRequests(value: boolean) {
        this.fields.auto_respond_invoice_requests = value;
    }

    get invoiceUploadRules(): Array<InvoiceUploadRule> {
        return this.fields.invoice_upload_rules;
    }

    set invoiceUploadRules(rules: Array<InvoiceUploadRule>) {
        this.fields.invoice_upload_rules = rules;
    }

    get notifyMissingInvoice(): boolean {
        return this.fields.notify_missing_invoice;
    }

    set notifyMissingInvoice(value: boolean) {
        this.fields.notify_missing_invoice = value;
    }

    get notifyMissingInvoiceMail(): string {
        return this.fields.notify_missing_invoice_mail;
    }

    set notifyMissingInvoiceMail(value: string) {
        this.fields.notify_missing_invoice_mail = value;
    }

    get includeUBLAttachment(): boolean {
        return this.fields.include_ubl_attachment;
    }

    set includeUBLAttachment(value: boolean) {
        this.fields.include_ubl_attachment = value;
    }

    get autoRespondOldOrders(): boolean {
        // Auto respond invoice requests for old orders
        return this.fields.auto_respond_old_orders;
    }

    set autoRespondOldOrders(value: boolean) {
        this.fields.auto_respond_old_orders = value;
    }

    get oldOrderInvoicePrefix(): string {
        return this.fields.old_order_invoice_prefix;
    }

    set oldOrderInvoicePrefix(value: string) {
        this.fields.old_order_invoice_prefix = value;
    }

    get oldOrderInvoiceStart(): string {
        return this.fields.old_order_invoice_start;
    }

    set oldOrderInvoiceStart(value: string) {
        this.fields.old_order_invoice_start = value;
    }

    get participateKOR(): boolean {
        return this.fields.participate_kor;
    }

    set participateKOR(value: boolean) {
        this.fields.participate_kor = value;
    }

    clone = (deep: boolean = true): Settings => {
        let fields = this.fields;
        if (deep) fields = deepClone(fields);
        return new Settings(fields);
    }

    protected build = (props?: Props) => {
        this.constructFields(props);
        if (props) {
            if (props.account) {
                this.account = props.account instanceof Account ? props.account :
                    new Account({...props.account, settings: this});
            }
        }
    }
}